import { PropsWithChildren } from "react";
import { useAuthorization } from "../hooks/useAuthorization";
import type { Permission, Role } from "../constants/permissions";

type GateProps = {
    /** Pass if user has any of these permissions. */
    anyOf?: Permission[];
    /** Pass if user has all of these permissions. */
    allOf?: Permission[];
    /** Pass if user has any of these role IDs. */
    anyRole?: Role[];
    /** Pass if user has all of these role IDs. */
    allRole?: Role[];
    fallback?: any;
};

/**
 * Conditionally render children based on RBAC permissions and/or role IDs.
 * Provided groups are ANDed together; items within anyOf/anyRole are ORed.
 */
export default function Gate({
    anyOf,
    allOf,
    anyRole,
    allRole,
    fallback = null,
    children,
}: PropsWithChildren<GateProps>) {
    const { can, hasRole } = useAuthorization();

    const anyPermOk = !anyOf || anyOf.some((p) => can(p));
    const allPermOk = !allOf || allOf.every((p) => can(p));
    const anyRoleOk = !anyRole || anyRole.some((r) => hasRole(r));
    const allRoleOk = !allRole || allRole.every((r) => hasRole(r));

    if (!anyPermOk || !allPermOk || !anyRoleOk || !allRoleOk) {
        return fallback;
    }

    return <>{children}</>;
}
